import styled from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  
  h1 {
    font-size: clamp(1.125rem, 3.5vw, 1.5rem);
    color: ${({ theme }) => theme.colors.gray[900]};
    min-width: 0;
    flex: 1 1 12rem;
  }

  & > button {
    flex-shrink: 0;
  }
`;

export const Actions = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

export const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  background-color: ${({ theme, variant }) =>
    variant === 'primary' ? theme.colors.primary.main :
      variant === 'danger' ? theme.colors.danger.main :
        '#fff'};
    
  color: ${({ theme, variant }) =>
    variant === 'primary' ? '#fff' :
      variant === 'danger' ? '#fff' :
        theme.colors.gray[900]};
    
  border: 1px solid ${({ theme, variant }) =>
    variant === 'outline' ? theme.colors.gray[200] : 'transparent'};

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const FiltersContainer = styled.div`
  display: flex;
  gap: 16px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  flex-wrap: wrap;
  align-items: flex-end;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1 1 10rem;
  max-width: 100%;
  
  label {
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray[900]};
  }
  
  input, select {
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.colors.gray[200]};
    border-radius: 4px;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    
    &:focus {
      border-color: ${({ theme }) => theme.colors.primary.main};
      outline: none;
    }
  }
`;

export const TableContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
  min-width: 0;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
  
  th, td {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray[100]};
    white-space: nowrap;
  }
  
  th {
    background-color: ${({ theme }) => theme.colors.background};
    font-weight: 600;
    color: ${({ theme }) => theme.colors.gray[900]};
    cursor: pointer;
    user-select: none;
    
    &:hover {
      background-color: ${({ theme }) => theme.colors.gray[100]};
    }
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:hover td {
    background-color: ${({ theme }) => theme.colors.background};
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  border-top: 1px solid ${({ theme }) => theme.colors.gray[100]};
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const PageInfo = styled.span`
  color: ${({ theme }) => theme.colors.gray[900]};
  font-size: 14px;
`;

export const PageButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

export const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  letter-spacing: 0.01em;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
  
  ${({ theme, 'data-color-token': token }) => {
    const bgByToken = {
      gray:   theme.colors.gray[100],
      amber:  theme.colors.warning?.light  || '#FFFBEB',
      blue:   theme.colors.info?.light     || '#EFF6FF',
      indigo: theme.colors.indigo?.light   || '#EEF2FF',
      teal:   theme.colors.teal?.light     || '#F0FDFA',
      orange: theme.colors.orange?.light   || '#FFF7ED',
      green:  theme.colors.success?.light  || '#F0FDF4',
      red:    theme.colors.danger.light,
    };
    const fgByToken = {
      gray:   theme.colors.gray[700]       || '#4B5563',
      amber:  theme.colors.warning?.dark   || '#92400E',
      blue:   theme.colors.info?.dark      || '#1D4ED8',
      indigo: theme.colors.indigo?.dark    || '#4338CA',
      teal:   theme.colors.teal?.dark      || '#0F766E',
      orange: theme.colors.orange?.dark    || '#C2410C',
      green:  theme.colors.success?.dark   || '#15803D',
      red:    theme.colors.danger.dark,
    };

    const bg = bgByToken[token] || theme.colors.gray[100];
    const fg = fgByToken[token] || theme.colors.gray[700] || '#4B5563';

    return `
      background-color: ${bg};
      color: ${fg};
    `;
  }}
`;

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: #fff;
  padding: clamp(16px, 3vw, 24px);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  min-width: 0;
`;

export const Input = styled.input`
  padding: 10px;
  border: 1px solid ${({ theme, $hasError }) =>
    $hasError ? theme.colors.danger.main : theme.colors.gray[200]};
  border-radius: 4px;
  
  &:focus {
    border-color: ${({ theme, $hasError }) =>
    $hasError ? theme.colors.danger.main : theme.colors.primary.main};
    outline: none;
  }
`;

export const Select = styled.select`
  padding: 10px;
  border: 1px solid ${({ theme, $hasError }) =>
    $hasError ? theme.colors.danger.main : theme.colors.gray[200]};
  border-radius: 4px;
  
  &:focus {
    border-color: ${({ theme, $hasError }) =>
    $hasError ? theme.colors.danger.main : theme.colors.primary.main};
    outline: none;
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  
  label {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray[900]};
  }
  
  span.error {
    color: ${({ theme }) => theme.colors.danger.main};
    font-size: 12px;
  }
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

export const InputGroup = styled.div`
  display: flex;
  gap: 16px;
  
  > div {
    flex: ${({ flex }) => flex || 1};
  }

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 16px;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary.main};
  margin: 24px 0 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};

  &:first-child {
    margin-top: 0;
  }
`;

export const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.danger.main};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;
  text-align: right;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }
`;

export const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  label {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.gray[900]};
    font-weight: 500;
    cursor: pointer;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #999999;

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #666666;
  }

  p {
    font-size: 14px;
  }
`;
